<template>
  <q-page class="legal-page">
    <div class="legal-container">
      <!-- Loading state -->
      <template v-if="loading">
        <div>
          <q-skeleton type="text" width="180px" dark style="font-size: 1.75rem;" />
          <q-skeleton type="text" width="300px" dark style="margin-top: 4px;" />
        </div>
        <q-skeleton v-for="n in 3" :key="n" type="rect" height="72px" dark style="border-radius: 12px; margin-top: 12px;" />
        <q-skeleton type="rect" height="160px" dark style="border-radius: 12px; margin-top: 24px;" />
      </template>

      <!-- Error state -->
      <div v-else-if="error" class="legal-empty">
        <q-icon name="error_outline" size="48px" style="color: var(--oa-danger);" />
        <p class="empty-text">{{ error }}</p>
        <q-btn
          no-caps
          unelevated
          label="Reintentar"
          style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 16px;"
          @click="fetchData"
        />
      </div>

      <!-- Loaded state -->
      <template v-else>
        <!-- Header -->
        <div class="legal-header">
          <h1 class="legal-title">Documentos Legales</h1>
          <p class="legal-subtitle">Acuerdos y compromisos requeridos para tu avance en Olympic Academy</p>
        </div>

        <!-- Pending Section -->
        <div class="legal-section">
          <div class="legal-section-header">
            <q-icon name="schedule" size="16px" style="color: var(--oa-gold-full);" />
            <h2 class="legal-section-title">Pendientes de firma</h2>
            <q-badge
              v-if="pending && pending.totalPending > 0"
              :label="String(pending.totalPending)"
              style="background: color-mix(in srgb, var(--oa-gold-full) 12%, transparent); color: var(--oa-gold-full); font-size: 10px; padding: 2px 8px; border-radius: 9999px; font-weight: 500;"
            />
          </div>

          <!-- All clear -->
          <div v-if="pending?.allClear" class="legal-all-clear">
            <q-icon name="check_circle" size="16px" style="color: var(--oa-success); flex-shrink: 0;" />
            <div>
              <p class="legal-all-clear-title">Todos los documentos firmados</p>
              <p class="legal-all-clear-desc">No tenes documentos pendientes.</p>
            </div>
          </div>

          <!-- Pending cards -->
          <div v-else-if="pending?.pending.length" class="legal-pending-list">
            <router-link
              v-for="doc in pending.pending"
              :key="doc.id"
              :to="{ name: 'legal-document', params: { documentId: doc.id } }"
              class="legal-pending-card"
            >
              <div class="legal-pending-left">
                <div class="legal-pending-icon" style="background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent); color: var(--oa-gold-full);">
                  <q-icon :name="typeIcon(doc.type)" size="16px" />
                </div>
                <div>
                  <p class="legal-pending-title">{{ doc.title }}</p>
                  <p v-if="doc.shortDescription" class="legal-pending-desc">{{ doc.shortDescription }}</p>
                </div>
              </div>
              <div class="legal-pending-right">
                <span v-if="doc.requiredAtStage" class="legal-stage-badge">
                  {{ STAGE_LABELS[doc.requiredAtStage] ?? doc.requiredAtStage }}
                </span>
                <span v-if="doc.hasAcceptedPrevious" class="legal-reaccept-badge">Re-aceptar</span>
                <span class="legal-sign-label">Leer y Firmar</span>
                <q-icon name="arrow_forward" size="14px" style="color: var(--oa-gold-mid);" />
              </div>
            </router-link>
          </div>
        </div>

        <!-- Signed Documents Section -->
        <div class="legal-section">
          <div class="legal-section-header">
            <q-icon name="check_circle" size="16px" style="color: var(--oa-success);" />
            <h2 class="legal-section-title">Mis documentos firmados</h2>
          </div>

          <div v-if="!myDocs?.length" class="legal-no-signed">
            Aun no firmaste ningun documento.
          </div>

          <div v-else class="legal-signed-list">
            <div
              v-for="(doc, i) in myDocs"
              :key="doc.id"
              class="legal-signed-row"
              :style="{ borderBottom: i < myDocs.length - 1 ? '1px solid var(--oa-border)' : undefined }"
            >
              <div class="legal-signed-left">
                <div class="legal-signed-icon">
                  <q-icon :name="typeIcon(doc.documentType)" size="14px" />
                </div>
                <div>
                  <p class="legal-signed-title">{{ doc.documentTitle }}</p>
                  <p class="legal-signed-meta">
                    v{{ doc.versionNumber }} &mdash; &ldquo;{{ doc.signatureText }}&rdquo;
                  </p>
                </div>
              </div>
              <div class="legal-signed-right">
                <q-badge
                  :label="doc.status === 'accepted' ? 'Vigente' : 'Reemplazado'"
                  :style="{
                    background: doc.status === 'accepted'
                      ? 'color-mix(in srgb, var(--oa-success) 10%, transparent)'
                      : 'var(--oa-bg-3)',
                    color: doc.status === 'accepted' ? 'var(--oa-success)' : 'var(--oa-text-disabled)',
                    fontSize: '10px',
                    padding: '2px 8px',
                    borderRadius: '6px',
                  }"
                />
                <span class="legal-signed-date">{{ formatDate(doc.acceptedAt) }}</span>
              </div>
            </div>
          </div>
        </div>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue';
import { api } from 'boot/axios';

// ---------- Types ----------
interface PendingDoc {
  id: string;
  type: string;
  title: string;
  shortDescription?: string;
  requiredAtStage?: string;
  currentVersionId?: string;
  hasAcceptedPrevious?: boolean;
}

interface PendingResponse {
  pending: PendingDoc[];
  totalPending: number;
  allClear: boolean;
}

interface SignedDoc {
  id: string;
  documentTitle: string;
  documentType: string;
  versionNumber: number;
  status: string;
  signatureText: string;
  acceptedAt: string;
  readTimeSeconds?: number;
}

// ---------- Constants ----------
const STAGE_LABELS: Record<string, string> = {
  pre_formacion: 'Pre-Formacion',
  formacion: 'Formacion',
  incorporacion: 'Incorporacion',
  ejecucion: 'Ejecucion',
};

const TYPE_ICONS: Record<string, string> = {
  nda: 'shield',
  precontrato: 'description',
  no_solicitacion: 'warning',
  no_competencia: 'warning',
  no_desprestigio: 'warning',
  confidencialidad_reforzada: 'shield',
  no_use_activos: 'shield',
  propiedad_intelectual: 'article',
  causales_falta_grave: 'warning',
};

// ---------- State ----------
const loading = ref(true);
const error = ref<string | null>(null);
const pending = ref<PendingResponse | null>(null);
const myDocs = ref<SignedDoc[]>([]);

// ---------- Helpers ----------
function typeIcon(docType: string): string {
  return TYPE_ICONS[docType] ?? 'description';
}

function formatDate(dateStr: string | undefined): string {
  if (!dateStr) return '--';
  try {
    const d = new Date(dateStr);
    return d.toLocaleDateString('es-AR', { day: '2-digit', month: '2-digit', year: 'numeric' });
  } catch {
    return dateStr;
  }
}

// ---------- Data Fetching ----------
async function fetchData() {
  loading.value = true;
  error.value = null;
  try {
    const [pendingRes, docsRes] = await Promise.all([
      api.get('/legal/pending'),
      api.get('/legal/my-documents'),
    ]);
    pending.value = pendingRes.data;
    myDocs.value = docsRes.data;
  } catch (err: unknown) {
    const msg = err instanceof Error ? err.message : 'Error al cargar documentos legales';
    error.value = msg;
  } finally {
    loading.value = false;
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  fetchData();
});
</script>

<style scoped>
.legal-page {
  padding: 24px;
}

.legal-container {
  max-width: 760px;
  margin: 0 auto;
}

.legal-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 40vh;
  gap: 12px;
}

.empty-text {
  font-size: 13px;
  color: var(--oa-text-muted);
}

.legal-header {
  margin-bottom: 24px;
}

.legal-title {
  font-size: 20px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.legal-subtitle {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 4px 0 0;
}

/* Sections */
.legal-section {
  margin-bottom: 32px;
}

.legal-section-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 12px;
}

.legal-section-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

/* All clear */
.legal-all-clear {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
  background: color-mix(in srgb, var(--oa-success) 6%, transparent);
  border: 1px solid var(--oa-success);
}

.legal-all-clear-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.legal-all-clear-desc {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 0;
}

/* Pending list */
.legal-pending-list {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.legal-pending-card {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 16px;
  border-radius: 12px;
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-gold-dim);
  text-decoration: none;
  transition: all 0.2s;
}

.legal-pending-card:hover {
  border-color: var(--oa-gold-full);
}

.legal-pending-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.legal-pending-icon {
  width: 36px;
  height: 36px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
}

.legal-pending-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.legal-pending-desc {
  font-size: 10px;
  color: var(--oa-text-muted);
  margin: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 280px;
}

.legal-pending-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.legal-stage-badge {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: var(--oa-bg-3);
  color: var(--oa-text-disabled);
}

.legal-reaccept-badge {
  font-size: 10px;
  padding: 2px 8px;
  border-radius: 9999px;
  background: color-mix(in srgb, var(--oa-tier-sc) 12%, transparent);
  color: var(--oa-tier-sc);
}

.legal-sign-label {
  font-size: 12px;
  font-weight: 500;
  color: var(--oa-gold-full);
}

/* No signed docs */
.legal-no-signed {
  font-size: 12px;
  color: var(--oa-text-muted);
  text-align: center;
  padding: 32px;
  background: var(--oa-bg-2);
  border-radius: 12px;
}

/* Signed list */
.legal-signed-list {
  border-radius: 12px;
  overflow: hidden;
  border: 1px solid var(--oa-border);
}

.legal-signed-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  background: var(--oa-bg-2);
}

.legal-signed-left {
  display: flex;
  align-items: center;
  gap: 12px;
}

.legal-signed-icon {
  width: 28px;
  height: 28px;
  border-radius: 6px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--oa-success) 10%, transparent);
  color: var(--oa-success);
  flex-shrink: 0;
}

.legal-signed-title {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.legal-signed-meta {
  font-size: 10px;
  color: var(--oa-text-disabled);
  margin: 0;
}

.legal-signed-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.legal-signed-date {
  font-size: 10px;
  color: var(--oa-text-disabled);
}

@media (max-width: 640px) {
  .legal-pending-card {
    flex-direction: column;
    align-items: flex-start;
    gap: 12px;
  }

  .legal-pending-right {
    width: 100%;
    justify-content: flex-end;
  }

  .legal-signed-row {
    flex-direction: column;
    align-items: flex-start;
    gap: 8px;
  }

  .legal-signed-right {
    width: 100%;
    justify-content: flex-end;
  }
}
</style>
